/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  RowData,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import React, {useState, FC, useEffect, useMemo} from 'react'
import {Row, Col, Pagination, Form, Spinner, Button, Dropdown} from 'react-bootstrap'
import {ListCountryResultRow} from '../core/_models'
import {Link, useNavigate} from 'react-router-dom'
import {getNameByISO3} from '../../../../libs/misc'

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    headerClass: string
    cellClass: string
  }
}

type ListCountryTableProps = {
  rows?: ListCountryResultRow[] | null
  year
  isLoading: boolean
  sourceType: string
}

type StatusType = 'found' | 'not_found' | null

const ListCountryTable: FC<ListCountryTableProps> = ({rows, year, isLoading, sourceType}) => {
  const navigate = useNavigate()
  const [internalLoading, setInternalLoading] = useState(false)
  const [listCountry, setListCountry] = useState<ListCountryResultRow[]>([])
  const [selectedSort, setSelectedSort] = useState<string | null>(null)
  const [displaySortText, setDisplaySortText] = useState('Sort by Country')

  const [pagination, setPagination] = useState({pageIndex: 0, pageSize: 10})

  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [selectedStatus, setSelectedStatus] = useState<StatusType>(null)

  const getSortParam = (sort: string | null) => {
    switch (sort) {
      case 'country_asc':
        return {
          displayText: 'Country Ascending',
          sorting: [{id: 'country', desc: false}],
        }
      case 'country_desc':
        return {
          displayText: 'Country Descending',
          sorting: [{id: 'country', desc: true}],
        }
      default:
        return {
          displayText: 'Sort by Country',
          sorting: [],
        }
    }
  }

  const handleSelectSort = (sort: string | null) => {
    if (selectedSort !== sort) {
      setPagination({
        pageIndex: 1,
        pageSize: 10,
      })
    }
    const value = sort === 'reset' ? null : sort
    setSelectedSort(value)

    const {displayText, sorting} = getSortParam(value)
    setDisplaySortText(displayText)
    setSorting(sorting) // this comes from props, passed from the main page
  }

  const handleSelectFilter = (status) => {
    if (selectedStatus !== status) {
      setPagination({
        pageIndex: 1,
        pageSize: 10,
      })
    }
    setSelectedStatus(status === 'reset' ? null : status)
  }

  const getDisplayFilterText = () => {
    switch (selectedStatus) {
      case 'found':
        return 'Data Found'
      case 'not_found':
        return 'Data Not Found'
      default:
        return 'Filter by Status'
    }
  }

  const columnHelper = createColumnHelper<ListCountryResultRow>()

  const columns = [
    columnHelper.accessor('tina_rep', {
      header: 'Country',
      cell: (info) => {
        return getNameByISO3(info.getValue())
      },
      meta: {
        headerClass: 'ps-4 text-start w-200px',
        cellClass: 'text-start',
      },
    }),
    // columnHelper.accessor('record_size', {
    //   header: `TINA's Availability`,
    //   cell: (info) => {
    //     return info.getValue() > 0 ? (
    //       <span className='data-status data-status-true'>Data Found</span>
    //     ) : (
    //       <span className='data-status data-status-false'>Data Not Found</span>
    //     )
    //   },
    //   meta: {
    //     headerClass: 'text-start w-150px',
    //     cellClass: 'text-start w-150px',
    //   },
    //   sortUndefined: 'last',
    // }),
    // columnHelper.accessor('publication_date', {
    //   header: `Comtrade's Availability`,
    //   cell: (info) => {
    //     const row = info.row.original
    //     const date = row.publication_date as string
    //     if (!date) return <span className='data-status data-status-false'>Data Not Found</span>

    //     const formattedDate = new Date(date)
    //     return (
    //       <span className='data-status data-status-true'>
    //         Data Found {` `}
    //         <span className='text-muted small'>
    //           last update: {` `}
    //           {formattedDate.toLocaleDateString('en-GB', {
    //             day: '2-digit',
    //             month: '2-digit',
    //             year: '2-digit',
    //           })}
    //         </span>
    //       </span>
    //     )
    //   },
    //   meta: {
    //     headerClass: 'text-start w-200px',
    //     cellClass: 'text-start w-200px',
    //   },
    //   sortUndefined: 'last',
    // }),
    columnHelper.display({
      id: 'data_availability',
      header: `Data's Availability`,
      cell: (info) => {
        const row = info.row.original

        const tinaAvailable = row.record_size > 0
        const comtradeAvailable = !!row.publication_date

        const formattedDate = row.publication_date
          ? new Date(row.publication_date).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: '2-digit',
            })
          : null

        const formattedUpdatedDate = row.updated_at
          ? new Date(row.updated_at).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: '2-digit',
            })
          : null

        return (
          <div className='d-flex flex-column gap-1'>
            {/* TINA */}
            <div className='d-flex align-items-center gap-2'>
              {tinaAvailable ? (
                <i className='fa-solid fa-circle-check text-success'></i>
              ) : (
                <i className='fa-solid fa-circle-xmark text-danger'></i>
              )}
              <span className='fw-semibold'>TINA</span>

              {tinaAvailable && (
                <span className='text-muted small'>updated: {formattedUpdatedDate ?? '-'}</span>
              )}
            </div>

            {/* COMTRADE / MANUAL */}
            <div className='d-flex align-items-center gap-2'>
              {comtradeAvailable ? (
                <i className='fa-solid fa-circle-check text-success'></i>
              ) : (
                <i className='fa-solid fa-circle-xmark text-danger'></i>
              )}
              <span className='fw-semibold'>
                {sourceType === 'comtrade' ? 'COMTRADE' : 'MANUAL'}
              </span>

              {comtradeAvailable && (
                <span className='text-muted small'>updated: {formattedDate ?? '-'}</span>
              )}
            </div>
          </div>
        )
      },
      meta: {
        headerClass: 'text-start w-250px',
        cellClass: 'text-start w-250px',
      },
    }),
    columnHelper.accessor('detail_country', {
      header: '',
      cell: (info) => {
        const row = info.row.original
        return (
          <Link to={`/trade-data/${row.tina_rep}?year=${year}&hs_vr=H4&source_type=${sourceType}`}>
            <span className='fa fa-eye'></span>
          </Link>
        )
      },
      meta: {
        headerClass: 'w-100px',
        cellClass: 'text-center',
      },
      enableSorting: false,
    }),
  ]

  // Cleanup function to reset the state when the component is unmounted
  useEffect(() => {
    setListCountry([])
    setInternalLoading(true)
    return () => {
      setListCountry([])
      setInternalLoading(false)
    }
  }, [])

  useEffect(() => {
    setInternalLoading(isLoading)
  }, [isLoading])

  useEffect(() => {
    setListCountry(rows!)
    setInternalLoading(false)
  }, [rows])

  const filteredData = useMemo(() => {
    if (!rows) return []

    return rows.filter((row) => {
      const matchStatus =
        selectedStatus === 'found'
          ? row.record_size > 0
          : selectedStatus === 'not_found'
          ? row.record_size < 1
          : true

      const matchSearch = globalFilter
        ? getNameByISO3(row.tina_rep).toLowerCase().includes(globalFilter.toLowerCase())
        : true

      return matchStatus && matchSearch
    })
  }, [rows, selectedStatus, globalFilter])

  const dataTable = useReactTable({
    data: filteredData || [],
    columns,
    state: {
      pagination,
      sorting,
      globalFilter,
    },
    pageCount: Math.ceil(filteredData.length / pagination.pageSize), // or use a fixed total if backend provides it
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  function paginationRange() {
    const currentPage = pagination.pageIndex + 1
    const totalPages = dataTable.getPageCount()
    const maxPagesToShow = 3
    const halfRange = Math.floor(maxPagesToShow / 2)

    // Ensure startPage and endPage are within valid range
    let startPage = Math.max(1, currentPage - halfRange)
    let endPage = Math.min(totalPages, currentPage + halfRange)

    // Adjust range to maintain maxPagesToShow
    if (endPage - startPage + 1 < maxPagesToShow) {
      if (currentPage - startPage >= halfRange) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1)
      } else {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1)
      }
    }

    const range: any = []

    // Add first page and ellipsis if necessary
    if (startPage > 1) {
      range.push(1)
      if (startPage > 2) {
        range.push('...')
      }
    }

    // Add pages in the calculated range
    for (let i = startPage; i <= endPage; i++) {
      range.push(i)
    }

    // Add last page and ellipsis if necessary
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        range.push('...')
      }
      range.push(totalPages)
    }

    return range
  }

  const handleNavigationPageChange = (nav) => {
    setPagination({
      ...pagination,
      pageIndex: nav === 'prev' ? Math.max(0, pagination.pageIndex - 1) : pagination.pageIndex + 1,
    })
  }

  const handleDisplayPageChange = (e) => {
    setPagination({
      ...pagination,
      pageIndex: 1,
      pageSize: +e.target.value,
    })
  }

  const pages = paginationRange()
  const firstIndexPerPages =
    dataTable.getState().pagination.pageIndex * dataTable.getState().pagination.pageSize + 1

  const lastIndexPerPages =
    dataTable.getState().pagination.pageIndex * dataTable.getState().pagination.pageSize +
    dataTable.getRowModel().rows.length
  const displayHeader = dataTable.getHeaderGroups().map((headerGroup) => (
    <tr className='fw-normal py-4 align-middle' key={headerGroup.id}>
      {headerGroup.headers.map((header) => {
        return (
          <th key={header.id} className={header.column.columnDef.meta?.headerClass}>
            <div
              className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
              title={
                header.column.getCanSort()
                  ? header.column.getNextSortingOrder() === 'asc'
                    ? 'Sort ascending'
                    : header.column.getNextSortingOrder() === 'desc'
                    ? 'Sort descending'
                    : 'Reset'
                  : undefined
              }
              onClick={header.column.getToggleSortingHandler()}
            >
              {flexRender(header.column.columnDef.header, header.getContext())}
              {header.column.getCanSort() && (
                <span className='ms-2'>
                  {{
                    asc: <i className='fa-solid fa-sort-up'></i>,
                    desc: <i className='fa-solid fa-sort-down'></i>,
                    false: <i className='fa-solid fa-sort'></i>,
                  }[header.column.getIsSorted() as string] ?? <i className='fa-solid fa-sort'></i>}
                </span>
              )}
            </div>
          </th>
        )
      })}
    </tr>
  ))
  const displayBody = dataTable.getRowModel().rows.map((row) => (
    <tr key={row.id} className='align-middle'>
      {row.getVisibleCells().map((cell) => {
        return (
          <td key={cell.id} className={cell.column.columnDef.meta?.cellClass}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        )
      })}
    </tr>
  ))
  return (
    <div style={{minHeight: '400px', position: 'relative'}}>
      {!internalLoading && (listCountry?.length ?? 0) > 0 ? (
        <>
          <Row className='mb-5 justify-content-between'>
            <Col lg={7} md={7}>
              <div className='d-flex justify-content-start align-items-center'>
                <i className='fa-solid fa-circle text-success fs-9 me-2'></i>Showing{' '}
                {firstIndexPerPages} to {lastIndexPerPages} of {filteredData.length} results
              </div>
              {sourceType === 'comtrade' ? (
                <>
                  <span className='text-muted small'>
                    <i>
                      *last updated at <strong>Comtrade</strong> is refering to publication date
                    </i>
                  </span>
                </>
              ) : (
                ''
              )}
            </Col>
            <Col lg={5} md={5} className='action-table'>
              <Form.Group className='form-group w-50' controlId=''>
                <div className='input-with-icon'>
                  <Form.Control
                    type='text'
                    className='form-control-md'
                    onChange={(e) => {
                      setPagination({
                        pageIndex: 1,
                        pageSize: 10,
                      })
                      setGlobalFilter(e.target.value)
                    }}
                    placeholder='Search'
                    value={globalFilter}
                  />
                  <div className='input-icon'>
                    <i className='fa-solid fa-magnifying-glass fa-rotate-90 text-primary'></i>
                  </div>
                </div>
              </Form.Group>
              <Form.Group className='form-group' controlId=''>
                <div className='input-with-icon'>
                  <Dropdown onSelect={handleSelectFilter}>
                    <Dropdown.Toggle
                      variant='light'
                      className='text-capitalize w-100 pe-10 text-start'
                    >
                      {getDisplayFilterText()}
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      <Dropdown.Item eventKey='found'>Data Found</Dropdown.Item>
                      <Dropdown.Item eventKey='not_found'>Data Not Found</Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item eventKey='reset' className='text-danger'>
                        Reset Filter
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>

                  <div className='input-icon'>
                    <i className='fa-solid fa-filter text-primary'></i>
                  </div>
                </div>
              </Form.Group>
            </Col>
          </Row>
          <table
            className='gy-3 table table-striped table-bordered table-hover table-header-sticky'
            id='list_country_tradeflow'
          >
            <thead className='table-dark'>{displayHeader}</thead>
            <tbody className='gy-1 overflow-auto'>{displayBody}</tbody>
          </table>
          <Row className='my-3'>
            <Col md={6} className='d-flex justify-content-start align-items-center'>
              Display
              <Form.Select
                size='sm'
                className='w-80px mx-2'
                onChange={handleDisplayPageChange}
                value={pagination.pageSize}
              >
                <option value='10'>10</option>
                <option value='20'>20</option>
                <option value='50'>50</option>
                <option value='100'>100</option>
              </Form.Select>
              list country
            </Col>
            <Col md={6} className='d-flex justify-content-end'>
              <Pagination>
                <Pagination.Prev
                  onClick={() => dataTable.previousPage()}
                  disabled={!dataTable.getCanPreviousPage()}
                />
                {/* Start Dynamic Page */}
                {pages.map((page, index) => {
                  if (page === '...') {
                    return <Pagination.Ellipsis key={index} />
                  }
                  return (
                    <Pagination.Item
                      key={index}
                      active={page === pagination.pageIndex + 1}
                      onClick={() => {
                        setPagination({
                          ...pagination,
                          pageIndex: page - 1, // Convert 1-based page to 0-based index
                        })
                      }}
                    >
                      {page}
                    </Pagination.Item>
                  )
                })}
                {/* End Dynamic Page */}
                <Pagination.Next
                  onClick={() => handleNavigationPageChange('next')}
                  disabled={pagination.pageIndex >= dataTable.getPageCount() - 1}
                />
              </Pagination>
            </Col>
          </Row>
        </>
      ) : (
        <div className='d-flex align-items-center justify-content-center' style={{height: '50vh'}}>
          <Spinner animation='border' role='status' variant='primary' className='me-3' />
          <div className='loading-text'>
            Loading
            <span className='dot'>.</span>
            <span className='dot'>.</span>
            <span className='dot'>.</span>
          </div>
        </div>
      )}
    </div>
  )
}

export {ListCountryTable}
